/**
 * 라이선스 표시 구분: 쿠폰 vs 결제
 * - paymentId 접두사 'COUPON-' (예: COUPON-EVT-xxx) 이면 쿠폰 발급
 * - 쿠폰이어도 기간연장(isExtension) 또는 업그레이드(purchaseType === 'upgrade')면 결제 라이선스로 표시
 */

const COUPON_PAYMENT_ID_PREFIX = 'COUPON-';

export interface LicenseLike {
  paymentId?: string;
  isExtension?: boolean;
  purchaseType?: string;
  previousPaymentId?: string;
}

/** paymentId가 COUPON- 접두사인지 (쿠폰으로 등록된 이용권) */
export function isCouponOriginLicense(license: LicenseLike | null | undefined): boolean {
  if (!license?.paymentId) return false;
  return String(license.paymentId).startsWith(COUPON_PAYMENT_ID_PREFIX);
}

/** 그룹 이용권 여부 (COUPON-GRP- 접두사) */
export function displayAsGroupLicense(license: LicenseLike | null | undefined): boolean {
  if (!license?.paymentId) return false;
  return String(license.paymentId).startsWith('COUPON-GRP-');
}

/** 관리자 발급 여부 (관리자 페이지에서 수동 발급한 그룹 이용권, batch.adminIssued 기반) */
export function displayAsAdminIssuedLicense(license: LicenseLike & { adminIssued?: boolean } | null | undefined): boolean {
  if (!license) return false;
  if (license.adminIssued === true) return true;
  return false;
}

/**
 * UI에서 "쿠폰 라이선스"로 표시할지 여부.
 * 쿠폰 발급이면서, 기간연장/업그레이드가 아닌 경우만 true.
 */
export function displayAsCouponLicense(license: LicenseLike | null | undefined): boolean {
  if (!license) return false;
  if (!isCouponOriginLicense(license)) return false;
  if (license.isExtension === true) return false;
  if (license.purchaseType === 'upgrade') return false;
  if (license.previousPaymentId) return false;
  return true;
}

/** 관리자 통계용: user 문서 데이터로 소스 구분 (Cloud Functions getPlanSegmentFromData와 동일 규칙) */
export type PlanSegment = 'individualPaid' | 'groupCoupon' | 'eventCoupon' | null;

export interface UserDataLike {
  userType?: string;
  license?: LicenseLike | null;
  upgradeCode?: unknown;
}

/** data.license 기준: 개별 유료 / 그룹 쿠폰 / 이벤트 쿠폰으로 분류. 구버전 upgradeCode는 이벤트 쿠폰으로 간주 */
export function getPlanSegmentFromUserData(data: UserDataLike | null | undefined): PlanSegment {
  const t = (data?.userType ?? 'free') as string;
  if (t !== 'basic' && t !== 'standard' && t !== 'pro') return null;
  const lic = data?.license;
  const hasLicense = lic != null && typeof lic === 'object';
  if (hasLicense) {
    const paymentId = lic.paymentId ? String(lic.paymentId) : '';
    const isCouponOrigin = paymentId.startsWith(COUPON_PAYMENT_ID_PREFIX);
    const noExtension = lic.isExtension !== true;
    const noUpgrade = lic.purchaseType !== 'upgrade' && !lic.previousPaymentId;
    if (isCouponOrigin && noExtension && noUpgrade) {
      if (paymentId.startsWith('COUPON-GRP-')) return 'groupCoupon';
      return 'eventCoupon';
    }
    return 'individualPaid';
  }
  if (data?.upgradeCode != null && typeof data.upgradeCode === 'object') return 'eventCoupon';
  return 'eventCoupon';
}
